// ReservationRequestsTab.js
import React, { useEffect, useMemo, useState } from 'react';
import api from '../services/api';

const ReservationRequestsTab = () => {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewedReservationIds, setViewedReservationIds] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('viewedReservationIds')) || [];
    } catch {
      return [];
    }
  });

  // أدوات العرض
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all | pending | approved | rejected

  const fetchReservations = async () => {
    setLoading(true);
    try {
      const res = await api.get('/reservations');
      const rawData = Array.isArray(res.data) ? res.data : [];

      // الأحدث أولاً
      const sorted = rawData.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );

      setReservations(sorted);

      // تحديث "المشاهَد" باستخدام تحديث دالّي لتفادي الاعتماد على قيمة خارجية
      const newPendingIds = sorted
        .filter((r) => r.status === 'pending')
        .map((r) => r._id);

      setViewedReservationIds((prev) => {
        const updated = Array.from(new Set([...prev, ...newPendingIds]));
        try {
          localStorage.setItem('viewedReservationIds', JSON.stringify(updated));
        } catch {}
        return updated;
      });

      setError('');
    } catch (err) {
      console.error(err);
      setError('فشل في تحميل طلبات الحجز.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReservations();
  }, []);

  const handleStatusChange = async (id, newStatus) => {
    try {
      await api.patch(`/reservations/${id}/status`, { status: newStatus });
      setReservations((prev) =>
        prev.map((res) => (res._id === id ? { ...res, status: newStatus } : res))
      );
    } catch (err) {
      console.error('فشل في تحديث الحالة', err);
      alert('حدث خطأ أثناء تحديث حالة الحجز.');
    }
  };

  const fmtDate = (v) => {
    if (!v) return '—';
    try {
      return new Date(v).toLocaleDateString();
    } catch {
      return String(v);
    }
  };

  const isNew = (createdAt) => {
    const d = new Date(createdAt);
    const now = new Date();
    const diffH = (now - d) / (1000 * 60 * 60);
    return diffH <= 24; // خلال 24 ساعة
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return reservations.filter((r) => {
      // فلترة الحالة
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      // بحث
      const user = String(r.user?.name || '').toLowerCase();
      const barcode = String(r.user?.barcode || '').toLowerCase();
      const book = String(r.book?.title || r.book?.name || '').toLowerCase();
      const match =
        !q || user.includes(q) || barcode.includes(q) || book.includes(q);
      return match;
    });
  }, [reservations, query, statusFilter]);

  return (
    <div className="container mx-auto max-w-7xl px-4 py-4" dir="rtl">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between mb-4">
        <h2 className="text-2xl md:text-3xl font-bold">📌 طلبات الحجز</h2>
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="🔎 بحث (مستخدم / باركود / كتاب)"
            className="border rounded p-2 w-full sm:w-64"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border rounded p-2 w-full sm:w-44"
          >
            <option value="all">كل الحالات</option>
            <option value="pending">قيد الانتظار</option>
            <option value="approved">تمت الموافقة</option>
            <option value="rejected">مرفوض</option>
          </select>
          <button
            onClick={fetchReservations}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            🔁 تحديث
          </button>
        </div>
      </div>

      {loading && <p className="text-gray-600">⏳ جاري التحميل...</p>}
      {error && <p className="text-red-600 mb-3">{error}</p>}

      {!loading && filtered.length === 0 && (
        <p className="text-gray-600">لا توجد طلبات مطابقة.</p>
      )}

      {/* بطاقات للموبايل */}
      <div className="grid gap-4 sm:hidden">
        {filtered.map((resv) => (
          <div
            key={resv._id}
            className={`bg-white border rounded-xl shadow p-4 ${
              resv.status === 'approved'
                ? 'ring-1 ring-green-200'
                : resv.status === 'rejected'
                ? 'ring-1 ring-red-200'
                : ''
            }`}
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="font-semibold text-blue-900">
                  {resv.user?.name || 'غير معروف'}
                </h3>
                <p className="text-sm text-gray-700">
                  🏷️ {resv.user?.barcode || '—'}
                </p>
              </div>
              {isNew(resv.createdAt) && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                  جديد
                </span>
              )}
            </div>

            <p className="mt-2 text-gray-800">
              📖 {resv.book?.title || resv.book?.name || '—'}
            </p>

            <div className="mt-2 text-sm">
              الحالة:{' '}
              <strong>
                {resv.status === 'approved'
                  ? '✅ تمت الموافقة'
                  : resv.status === 'rejected'
                  ? '❌ مرفوض'
                  : '⏳ بانتظار المعالجة'}
              </strong>
            </div>

            <div className="mt-1 text-sm text-gray-700">
              📅 {fmtDate(resv.createdAt)}
            </div>

            <div className="mt-3 flex flex-wrap gap-2">
              {resv.status === 'pending' ? (
                <>
                  <button
                    onClick={() => handleStatusChange(resv._id, 'approved')}
                    className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
                  >
                    ✅ قبول
                  </button>
                  <button
                    onClick={() => handleStatusChange(resv._id, 'rejected')}
                    className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700"
                  >
                    ❌ رفض
                  </button>
                </>
              ) : (
                <span className="text-gray-500">تمت المعالجة</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* جدول للشاشات الأوسع */}
      {filtered.length > 0 && (
        <div className="hidden sm:block">
          <div className="overflow-x-auto rounded-xl shadow border border-gray-300 bg-white">
            <table className="min-w-full text-sm text-right">
              <thead className="bg-gray-100 text-gray-700 sticky top-0">
                <tr>
                  <th className="p-3 border">المستخدم</th>
                  <th className="p-3 border">الباركود</th>
                  <th className="p-3 border">عنوان الكتاب</th>
                  <th className="p-3 border">الحالة</th>
                  <th className="p-3 border">📅 تاريخ الطلب</th>
                  <th className="p-3 border">الإجراء</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filtered.map((resv) => (
                  <tr
                    key={resv._id}
                    className={`text-center hover:bg-gray-50 transition ${
                      resv.status === 'approved'
                        ? 'bg-green-50'
                        : resv.status === 'rejected'
                        ? 'bg-red-50'
                        : ''
                    }`}
                  >
                    <td className="p-2 border">
                      <div className="flex items-center justify-between gap-2">
                        <span>{resv.user?.name || 'غير معروف'}</span>
                        {isNew(resv.createdAt) && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                            جديد
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-2 border">{resv.user?.barcode || '—'}</td>
                    <td className="p-2 border">
                      {resv.book?.title || resv.book?.name || '—'}
                    </td>
                    <td className="p-2 border">
                      {resv.status === 'approved'
                        ? '✅ تمت الموافقة'
                        : resv.status === 'rejected'
                        ? '❌ مرفوض'
                        : '⏳ بانتظار المعالجة'}
                    </td>
                    <td className="p-2 border">{fmtDate(resv.createdAt)}</td>
                    <td className="p-2 border">
                      {resv.status === 'pending' ? (
                        <div className="flex justify-center gap-2">
                          <button
                            onClick={() => handleStatusChange(resv._id, 'approved')}
                            className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
                          >
                            ✅ قبول
                          </button>
                          <button
                            onClick={() => handleStatusChange(resv._id, 'rejected')}
                            className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700"
                          >
                            ❌ رفض
                          </button>
                        </div>
                      ) : (
                        <span className="text-gray-500">تمت المعالجة</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReservationRequestsTab;
